import React from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
    Button,
    Grid,
    FormControlLabel,
    Switch,
    FormControl,
    InputLabel,
    MenuItem,
    FormHelperText,
    Select as MuiSelect,

} from "@mui/material";

import AppForm from "components/Shared/AppForm";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";

import { useAuth } from "hooks/useAuth";

const INITIAL_STATE = {
    isActive: false,
    providers: "",
    account: "",
    entity: "",
};

const PaymentIntegrationForm = ({
    paymentIntegrationDetail = null,
    isBusy = false,
    onSubmit = () => { },
}) => {
    const navigate = useNavigate();
    const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

    const providers = ["NCHL", "Fonepay"];

    // RTK Query
    const { data: accounts, isLoading: loadingAccounts } = useGetDDLAccountListQuery();
    const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
        useLazyGetDDLEntityListQuery();

    // react form hook
    const {
        control,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
    } = useForm({
        defaultValues: {
            ...INITIAL_STATE,
            entity: viewEntity?.entity,
            account: viewEntity?.entity?.account || "",
            isActive: paymentIntegrationDetail?.isActive ?? false,
            providers: paymentIntegrationDetail?.providers ?? "",
        },
    });

    const _watchAccount = useWatch({ control, name: "account" });

    // methods
    const handleOnPaymentIntegrationSubmit = (data) => {
        const formattedData = {
            ...data,
            account: data?.account?._id,
            entity: data?.entity?._id,
        };
        onSubmit(formattedData);
    };

    React.useEffect(() => {
        if (!paymentIntegrationDetail) return;

        reset({
            ...INITIAL_STATE,
            ...paymentIntegrationDetail,
            entity: viewEntity?.entity,
            account: viewEntity?.entity?.account || "",
        });
    }, [paymentIntegrationDetail, reset, viewEntity?.entity]);

    React.useEffect(() => {
        if (isAccountAdmin || isSystemAdmin) {
            fetchEntities({
                account: _watchAccount?._id || viewEntity?.entity?.account,
            });
        }
    }, [isAccountAdmin, isSystemAdmin, _watchAccount?._id, fetchEntities, viewEntity?.entity?.account]);

    return (
        <AppForm loading={isBusy || loadingAccounts || loadingEntities}>
            <AppForm.Content>
                <Grid container spacing={4}>
                    {(isAccountAdmin || isSystemAdmin) && (
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="account"
                                control={control}
                                rules={{ required: "Please select an account." }}
                                render={({ field: { value, onChange } }) => (
                                    <BaseAutocomplete
                                        fullWidth
                                        label="Account"
                                        required
                                        value={value ?? null}
                                        onChange={(_, data) => {
                                            onChange(data);
                                            setValue("entity", null);
                                        }}
                                        getOptionLabel={(option) => option?.name ?? ""}
                                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                        options={accounts || []}
                                        loading={loadingAccounts}
                                        loadingText="Loading Account..."
                                        disabled
                                        helperText={errors?.account?.message}
                                        error={!!errors?.account}
                                    />
                                )}
                            />
                        </Grid>
                    )}

                    {(isAccountAdmin || isSystemAdmin) && (
                        <Grid item xs={12} sm={6}>
                            <Controller
                                name="entity"
                                control={control}
                                rules={{ required: "Please select an entity." }}
                                render={({ field: { value, onChange } }) => (
                                    <BaseAutocomplete
                                        fullWidth
                                        required
                                        label="Entity"
                                        value={value ?? null}
                                        onChange={(_, data) => onChange(data)}
                                        getOptionLabel={(option) => option?.name ?? ""}
                                        isOptionEqualToValue={(option, value) => option?._id === value?._id}
                                        options={entities || []}
                                        loading={loadingEntities}
                                        disabled
                                        loadingText="Loading Entities..."
                                        helperText={errors?.entity?.message}
                                        error={!!errors?.entity}
                                    />
                                )}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="providers"
                            control={control}
                            rules={{ required: "Please select a Provider." }}
                            render={({ field, value }) => (
                                <FormControl fullWidth size="small" error={!!errors.providers}>
                                    <InputLabel id="select-provider">Provider</InputLabel>
                                    <MuiSelect
                                        labelId="select-provider"
                                        label="Provider"
                                        placeholder="Select Provider"
                                        size="small"
                                        value={value ? value : ""}
                                        {...field}
                                    >
                                        {providers?.map((provider) => (
                                            <MenuItem key={provider} value={provider}>
                                                {provider}
                                            </MenuItem>
                                        ))}
                                    </MuiSelect>
                                    {!!errors.providers && (
                                        <FormHelperText>{errors.providers.message}</FormHelperText>
                                    )}
                                </FormControl>
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="isActive"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={field.value}
                                            onChange={field.onChange}
                                            color="primary"
                                        />
                                    }
                                    label="Is Active"
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </AppForm.Content>

            <AppForm.Actions>
                <Button onClick={() => navigate(-1)}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit(handleOnPaymentIntegrationSubmit)}
                >
                    {paymentIntegrationDetail ? "Update" : "Add"}
                </Button>
            </AppForm.Actions>
        </AppForm>
    );
};

export default PaymentIntegrationForm;